import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import ProfileSubNav from '../components/shared/ProfileSubNav';
import { Icon } from '../utils/icons';
import { useLang } from '../i18n';
import { getFavorites, removeFavorite } from '../utils/favorites';

const SPORT_COLOR = {
  football:        '#0f891f',
  basketball:      '#fca601',
  tennis:          '#f8c83c',
  'ice-hockey':    '#3bc8f6',
  volleyball:      '#f67c3b',
  rugby:           '#a76b2f',
  handball:        '#e85cd1',
  'table-tennis':  '#f6c344',
  mma:             '#e84040',
  'american-football': '#ff6b35',
};

const css = `
  .fav-page { padding: 6px 16px 32px; }
  .fav-empty { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 64px 20px; color: #5a5972; text-align: center; }
  .fav-empty-title { font-size: 15px; font-weight: 700; color: #cfcee0; }
  .fav-empty-sub { font-size: 13px; color: #757490; line-height: 1.5; max-width: 240px; }
  .fav-hint { display: flex; align-items: center; gap: 8px; margin-top: 8px; padding: 10px 14px; border-radius: 10px; background: rgba(118,225,255,.07); box-shadow: inset 0 0 0 1px rgba(118,225,255,.14); font-size: 12px; color: #9b9ab0; line-height: 1.45; }
  .fav-section { margin-bottom: 20px; }
  .fav-section-label { font-size: 11px; font-weight: 800; color: #757490; letter-spacing: .6px; text-transform: uppercase; margin-bottom: 8px; }
  .fav-list { display: flex; flex-direction: column; gap: 1px; border-radius: 12px; overflow: hidden; }
  .fav-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: #16142e; cursor: pointer; }
  .fav-row:active { background: #1a1938; }
  .fav-ic { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; flex: none; }
  .fav-name { flex: 1; min-width: 0; }
  .fav-name-main { font-size: 14px; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fav-name-sub { font-size: 11px; color: #9b9ab0; margin-top: 2px; }
  .fav-remove { width: 30px; height: 30px; border-radius: 8px; background: rgba(255,93,108,.1); display: grid; place-items: center; flex: none; }
  .fav-remove:hover { background: rgba(255,93,108,.2); }
  .fav-clear { display: flex; align-items: center; justify-content: center; gap: 6px; height: 40px; border-radius: 9px; border: none; background: rgba(255,93,108,.08); box-shadow: inset 0 0 0 1px rgba(255,93,108,.18); color: #ff5d6c; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; width: 100%; margin-top: 8px; }
`;

const SPORT_ROUTE = {
  football: '/football', basketball: '/basketball', tennis: '/tennis',
  'ice-hockey': '/ice-hockey', volleyball: '/volleyball', rugby: '/rugby',
  handball: '/handball', 'table-tennis': '/table-tennis', mma: '/mma',
  'american-football': '/american-football',
};

export default function Favoris() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [favs, setFavs] = useState(getFavorites);

  useEffect(() => {
    const update = () => setFavs(getFavorites());
    window.addEventListener('favoritesChanged', update);
    return () => window.removeEventListener('favoritesChanged', update);
  }, []);

  const remove = (id, type) => removeFavorite(id, type);

  const leagues = favs.filter((f) => f.type === 'league');
  const teams   = favs.filter((f) => f.type === 'team');
  const matches = favs.filter((f) => f.type === 'match');

  const goTo = (f) => {
    if (f.href) { navigate(f.href); return; }
    if (f.type === 'league' && f.sport) navigate(SPORT_ROUTE[f.sport] || '/football');
    if (f.type === 'match'  && f.sport && f.matchId) navigate(`/match/${f.sport}/${f.matchId}`);
  };

  if (favs.length === 0) {
    return (
      <PhoneShell activeTab="">
        <style>{css}</style>
        <BackHeader title={t('favorites.title')} />
        <ProfileSubNav />
        <div className="fav-page">
          <div className="fav-empty">
            <svg width="54" height="54" viewBox="0 0 24 24" fill="none">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" stroke="#5a5972" strokeWidth="1.2" strokeLinejoin="round" />
            </svg>
            <div>
              <div className="fav-empty-title">{t('favorites.empty')}</div>
              <div className="fav-empty-sub">{t('favorites.emptySub')}</div>
            </div>
            <div className="fav-hint">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#76e1ff" strokeWidth="1.5" />
                <path d="M12 8v4m0 4h.01" stroke="#76e1ff" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {t('favorites.hint')}
            </div>
          </div>
        </div>
      </PhoneShell>
    );
  }

  const Section = ({ label, items }) => items.length === 0 ? null : (
    <div className="fav-section">
      <div className="fav-section-label">{label}</div>
      <div className="fav-list">
        {items.map((f) => {
          const color = SPORT_COLOR[f.sport] || '#76e1ff';
          return (
            <div className="fav-row" key={f.id + f.type} onClick={() => goTo(f)}>
              <span className="fav-ic" style={{ background: color + '22' }}>
                <Icon name={`sport-${f.sport || 'football'}`} w={18} h={18} color={color} />
              </span>
              <div className="fav-name">
                <div className="fav-name-main">{f.name}</div>
                {f.meta ? <div className="fav-name-sub">{f.meta}</div> : null}
              </div>
              <button className="fav-remove" onClick={(e) => { e.stopPropagation(); remove(f.id, f.type); }} aria-label={t('favorites.remove')}>
                <svg width="12" height="12" viewBox="0 0 12 12">
                  <path d="M2 2l8 8M10 2L2 10" stroke="#ff5d6c" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={t('favorites.title')} />
      <ProfileSubNav />
      <div className="fav-page">
        <Section label={t('favorites.competitions')} items={leagues} />
        <Section label={t('favorites.teams')}        items={teams}   />
        <Section label={t('favorites.matches')}      items={matches} />
      </div>
    </PhoneShell>
  );
}
